import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogContent,
    Divider,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import { Google } from '@mui/icons-material';
import React, { useState } from 'react';
import TermsAcceptanceCheckbox from './TermsAcceptanceCheckbox';
import { colors } from '../theme';
import CancelButton from './CancelButton';

interface GoogleSignInCompleteModalProps {
  open: boolean;
  userRole: 'dietitian' | 'client';
  email: string;
  firstName: string;
  lastName: string;
  onComplete: (data: {
    firstName: string;
    lastName: string;
    phone?: string;
    age?: number;
    weight?: number;
    height?: number;
    gender?: string;
    acceptsOnlineClients?: boolean;
    termsAccepted?: boolean;
  }) => Promise<void>;
  onCancel: () => Promise<void>;
  loading?: boolean;
}

const GoogleSignInCompleteModal: React.FC<GoogleSignInCompleteModalProps> = ({
  open,
  userRole,
  email,
  firstName: initialFirstName,
  lastName: initialLastName,
  onComplete,
  onCancel,
  loading = false,
}) => {
  const [firstName, setFirstName] = useState(initialFirstName);
  const [lastName, setLastName] = useState(initialLastName);
  const [phone, setPhone] = useState('');
  const [age, setAge] = useState('');
  const [weight, setWeight] = useState('');
  const [height, setHeight] = useState('');
  const [gender, setGender] = useState('');
  const [acceptsOnlineClients, setAcceptsOnlineClients] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    setError('');
    setTermsError(false);

    // Validasyon
    if (!firstName.trim() || !lastName.trim()) {
      setError('Lütfen adınızı ve soyadınızı giriniz.');
      return;
    }
    if (!termsAccepted) {
      setTermsError(true);
      setError('Devam etmek için kullanım şartlarını kabul etmelisiniz.');
      return;
    }

    const ageNum = age.trim() ? parseInt(age.trim(), 10) : undefined;
    const weightNum = weight.trim() ? parseFloat(weight.trim()) : undefined;
    const heightNum = height.trim() ? parseFloat(height.trim()) : undefined;
    try {
      await onComplete({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim() || undefined,
        age: ageNum != null && !isNaN(ageNum) ? ageNum : undefined,
        weight: weightNum != null && !isNaN(weightNum) ? weightNum : undefined,
        height: heightNum != null && !isNaN(heightNum) ? heightNum : undefined,
        gender: gender.trim() || undefined,
        acceptsOnlineClients: userRole === 'dietitian' ? acceptsOnlineClients : undefined,
        termsAccepted: true,
      });
    } catch (err: any) {
      setError(err.message || 'Bir hata oluştu. Lütfen tekrar deneyin.');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: 'hidden',
        },
      }}
    >
      {/* Header with Google Branding */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #4285F4 0%, #34A853 50%, #FBBC05 100%)',
          p: 3,
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: -50,
            right: -50,
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.1)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -30,
            left: -30,
            width: 150,
            height: 150,
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.1)',
          }}
        />
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                background: 'rgba(255, 255, 255, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2,
                backdropFilter: 'blur(10px)',
              }}
            >
              <Google sx={{ fontSize: 28, color: 'white' }} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
                Profil Bilgilerinizi Tamamlayın
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.95 }}>
                Google hesabınızla giriş yaptınız
              </Typography>
            </Box>
          </Box>
          <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.875rem' }}>
            Hesabınızı tamamlamak için aşağıdaki bilgileri doldurun
          </Typography>
        </Box>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        <Box component="form" sx={{ mt: 1 }}>
          <TextField
            margin="normal"
            required
            fullWidth
            id="firstName"
            label="Adınız"
            name="firstName"
            autoComplete="given-name"
            autoFocus
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="Ahmet"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover fieldset': {
                  borderColor: colors.primary.main,
                },
                '&.Mui-focused fieldset': {
                  borderColor: colors.primary.main,
                  borderWidth: 2,
                },
              },
            }}
          />

          <TextField
            margin="normal"
            required
            fullWidth
            id="lastName"
            label="Soyadınız"
            name="lastName"
            autoComplete="family-name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Yılmaz"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover fieldset': {
                  borderColor: colors.primary.main,
                },
                '&.Mui-focused fieldset': {
                  borderColor: colors.primary.main,
                  borderWidth: 2,
                },
              },
            }}
          />

          <TextField
            margin="normal"
            fullWidth
            id="phone"
            label="Telefon (Opsiyonel)"
            name="phone"
            autoComplete="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="0555 123 45 67"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover fieldset': {
                  borderColor: colors.primary.main,
                },
                '&.Mui-focused fieldset': {
                  borderColor: colors.primary.main,
                  borderWidth: 2,
                },
              },
            }}
          />

          {userRole === 'client' && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
                Kilo, boy, yaş ve cinsiyet (opsiyonel – diyetisyeninizle paylaşılır)
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <TextField
                  margin="normal"
                  type="number"
                  inputProps={{ min: 30, max: 300, step: 0.1 }}
                  label="Kilo (kg)"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  placeholder="75"
                  size="small"
                  sx={{ flex: '1 1 100px', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                <TextField
                  margin="normal"
                  type="number"
                  inputProps={{ min: 100, max: 250, step: 0.5 }}
                  label="Boy (cm)"
                  value={height}
                  onChange={(e) => setHeight(e.target.value)}
                  placeholder="170"
                  size="small"
                  sx={{ flex: '1 1 100px', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                <TextField
                  margin="normal"
                  type="number"
                  inputProps={{ min: 10, max: 120 }}
                  label="Yaş"
                  value={age}
                  onChange={(e) => setAge(e.target.value)}
                  placeholder="28"
                  size="small"
                  sx={{ flex: '1 1 80px', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                <FormControl size="small" margin="normal" sx={{ flex: '1 1 120px', minWidth: 120, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                  <InputLabel id="modal-gender-label">Cinsiyet</InputLabel>
                  <Select
                    labelId="modal-gender-label"
                    label="Cinsiyet"
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                  >
                    <MenuItem value="">Seçiniz</MenuItem>
                    <MenuItem value="Erkek">Erkek</MenuItem>
                    <MenuItem value="Kadın">Kadın</MenuItem>
                    <MenuItem value="Diğer">Diğer</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
          )}

          {userRole === 'dietitian' && (
            <Box
              sx={{
                mt: 2.5,
                mb: 1,
                p: 2,
                borderRadius: 2,
                background: colors.alpha.primary05,
                border: `1px solid ${colors.primary.light}`,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={acceptsOnlineClients}
                    onChange={(e) => setAcceptsOnlineClients(e.target.checked)}
                    sx={{
                      color: colors.primary.main,
                      '&.Mui-checked': {
                        color: colors.primary.main,
                      },
                    }}
                  />
                }
                label={
                  <Typography variant="body2" fontWeight={600}>
                    Online danışan almak istiyorum
                  </Typography>
                }
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ ml: 4, display: 'block', mt: 0.5, fontSize: '0.8rem' }}
              >
                Bu seçeneği işaretlerseniz, danışanlar sizi seçebilecek ve
                iletişime geçebilecekler.
              </Typography>
            </Box>
          )}

          <TermsAcceptanceCheckbox
            checked={termsAccepted}
            onChange={(v) => { setTermsAccepted(v); setTermsError(false); }}
            error={termsError}
            disabled={loading}
          />

          {error && (
            <Box
              sx={{
                mt: 2,
                mb: 1,
                p: 1.5,
                borderRadius: 2,
                background: '#ffebee',
                border: '1px solid #ef5350',
              }}
            >
              <Typography variant="body2" color="error" sx={{ fontSize: '0.875rem' }}>
                {error}
              </Typography>
            </Box>
          )}

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
            <CancelButton
              fullWidth
              onClick={onCancel}
              disabled={loading}
              sx={{ py: 1.5 }}
            />
            <Button
              fullWidth
              variant="contained"
              onClick={handleSubmit}
              disabled={loading}
              sx={{
                borderRadius: 2,
                py: 1.5,
                background: colors.gradients.primary,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: colors.shadows.button,
                '&:hover': {
                  background: colors.gradients.primaryMedium,
                  boxShadow: colors.shadows.cardHover,
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              {loading ? 'Kaydediliyor...' : 'Kayıt Oluştur'}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default GoogleSignInCompleteModal;

